import React from "react";
import PropTypes from "prop-types";
import {connect} from "react-redux";
import { moneyToString } from "lib/money";
import Button from "components/Button";
import Spinner from "components/Spinner";
import { processSecureTradingPaymentResponse, clearWalletPayJWTString, closeCheckout, saveAudit, processSecureTradingToken } from "state/school_money/actions";
import { getCheckoutTotalToPay, getCheckoutStatus } from "state/school_money/selectors";
import { getUserIdentityAppReferrer } from "state/app/selectors";

import "./PaymentTypeSelector.css";

const ONLINE_PAY_BUTTON_CHECK_DELAY = 500;
const GOOGLE_PAY_BUTTON_ID = "google-pay";
const APPLE_PAY_BUTTON_ID = "st-apple-pay";

class PaymentTypeSelector extends React.Component{

  static propTypes = {
    walletType: PropTypes.string.isRequired,
    walletJWTString: PropTypes.string.isRequired,
  }

  constructor(props) {
    super(props);
    this.state = {
      secureTradingReference: undefined,
      isLoading: true,
      onlinePayButtonCheckInterval: undefined,
      loadingFrameInfo: "Preparing payment options.",
      setupError: undefined
    }
  }

  componentDidMount() {
    try{

      this.setState({ onlinePayButtonCheckInterval: setInterval(this.checkWalletPayButton, ONLINE_PAY_BUTTON_CHECK_DELAY) });

      if( this.props.walletType === "google" ) {
        const paymentsClient = new window.google.payments.api.PaymentsClient({environment: process.env.REACT_APP_SECURE_TRADING_PAYMENT_MODE});
        const container = document.getElementById(GOOGLE_PAY_BUTTON_ID);
        const button = paymentsClient.createButton({
          buttonColor: "default",
          buttonType: "pay",
          buttonSizeMode: "fill",
          onClick: this.initiateGooglePay,
        });

        container.appendChild(button);

        this.props.saveAudit("ST_GOOGLE_PAY_TOKEN_GENERATED");
      } else {
        var st = window.SecureTrading({
          jwt: this.props.walletJWTString,
          submitOnSuccess: false,
          submitCallback: this.processPaymentResponse,
          disableNotification: true,
          livestatus: Number(process.env.REACT_APP_SECURE_TRADING_3DS_LIVESTATUS)
        });
        st.ApplePay(this.getApplePaySetup());
        this.props.saveAudit("ST_APPLE_PAY_TOKEN_GENERATED");
      }
    }catch(error){
      this.setState({ setupError: error });
    }
    this.setState({ secureTradingReference: st });
  }

  getWalletButtonID = () => {
    return this.props.walletType === "apple" ? APPLE_PAY_BUTTON_ID : GOOGLE_PAY_BUTTON_ID;
  }

  getGooglePaySetup = () => {
    return {
      JWT: this.props.walletJWTString,
      ApiVersion: 2,
      ApiVersionMinor: 0,
      AllowedPaymentMethod: [{
        AllowedPaymentMethodParameters: {
          AllowedAuthMethods: ["PAN_ONLY", "CRYPTOGRAM_3DS"],
          AllowedCardNetworks: ["VISA", "MASTERCARD"]
        },
        TokenizationSpecification: {
          Parameters: {
            Gateway: "trustpayments",
            GatewayMerchantId: process.env.REACT_APP_SECURE_TRADING_SITE_REFERENCE
          },
          Type: "PAYMENT_GATEWAY"
        },
        Type: "CARD"
      }],
      TransactionInfo: {
        CountryCode: "GB",
        CurrencyCode: "GBP",
        TotalPriceStatus: "FINAL",
        TotalPrice: moneyToString(Math.abs(this.props.totalToPay)).toString()
      },
      MerchantInfo: {
        MerchantId: process.env.REACT_APP_SECURE_TRADING_GOOGLE_PAY_MERCHANT_ID,
        MerchantName: "SchoolMoney"
      },
      scaexemptionindicator: 14
    }
  }

  getApplePayeMerchantId = () => {
    return this.props.appReferrer === "xpressions" ? process.env.REACT_APP_SECURE_TRADING_XPRESSIONS_APPLE_PAY_MERCHANT_ID : process.env.REACT_APP_SECURE_TRADING_SCHOOL_MONEY_APPLE_PAY_MERCHANT_ID;
  }

  getApplePaySetup = () => {
    return {
      buttonStyle: "black",
      buttonText: "plain",
      merchantId: this.getApplePayeMerchantId(),
      paymentRequest: {
          countryCode: "GB",
          currencyCode: "GBP",
          merchantCapabilities: ["supports3DS", "supportsCredit", "supportsDebit"],
          supportedNetworks: ["visa", "masterCard"],
          total: {
              label: "Schoolmoney",
              amount: this.props.totalToPay.toString()
          }
      },
      buttonPlacement: this.getWalletButtonID()
    };
  }

  checkWalletPayButton = () => {
    if( document.getElementById(this.getWalletButtonID()).innerHTML !== "" ){
      this.setState({ isLoading: false });
      clearInterval(this.state.onlinePayButtonCheckInterval);
    }
  }

  processPaymentResponse = (response) => {
    if( response.errorcode === "cancelled" ){
      this.props.onClose();
    }else{
      this.props.processPaymentResponse(response);
    }
  }

  initiateCardPayment = () => {
    if( this.props.walletType === "apple" ){
      this.state.secureTradingReference.destroy();
    }
    this.props.initiateCardPayment();
  }

  displayLoadingFrame = () => {
    this.setState({ isLoading: true });
    this.setState({ loadingFrameInfo: "Processing payment." });
    this.props.saveAudit("PAYMENT_PROCESS_STARTED");
  }

  initiateGooglePay = () => {
    this.props.saveAudit("GOOGLE_PAY_PAYMENT_PROCESS_STARTED");
    this.props.setProcessingStatus();
    this.props.saveAudit("Google payload for native: " + JSON.stringify(this.getGooglePaySetup()));
    window.android.gpBridgeCallback(JSON.stringify(this.getGooglePaySetup()));
  }

  getWalletPayElements() {
    if(this.props.walletType === "apple") {
      return (
        <div>
          <form id="st-form" action="#"/>
          <div className="payment-type-selector__apple-pay-button-container">
            <div id="st-apple-pay" className="payment-type-selector__apple-pay-button"></div>
          </div>
        </div>
      );
    } else {
      return (<div id="google-pay" className="payment-type-selector__google-pay-button"></div>);
    }
  }

  render() {
    const paymentTypeSelectorStlye = this.state.isLoading ? {display: "none"} : {display: "block"};
    const loadingFrameStyle = this.state.isLoading ? {display: "block"} : {display: "none"};
    return (
      <div>
        <div style={loadingFrameStyle} className="payment-type-selector__spinner">
          <div>{this.state.loadingFrameInfo} Please wait...</div>
          <Spinner size="standalone" color="school_money" />
        </div>
        <div style={paymentTypeSelectorStlye} className="payment-type-selector__container">
          <div className="payment-type-selector__button-container">
            <Button className="payment-type-selector__pay-button" onClick={ this.initiateCardPayment } style={ {borderRadius: "5px"} }>Pay with Card</Button>
          </div>
          { this.getWalletPayElements() }
          <div className="payment-type-selector__button-container">
            <Button className="payment-type-selector__cancel-button" onClick={ this.props.onClose } style={ {borderRadius: "5px"} }>Cancel</Button>
          </div>
        </div>
      </div>
    );
  }
}

export default connect(
  (state) => ({
    totalToPay: getCheckoutTotalToPay(state),
    status: getCheckoutStatus(state),
    appReferrer: getUserIdentityAppReferrer(state)
  }),
  {
    processPaymentResponse: processSecureTradingPaymentResponse,
    initiateCardPayment: clearWalletPayJWTString,
    onClose: closeCheckout,
    saveAudit: saveAudit,
    setProcessingStatus: processSecureTradingToken,
  }
)(PaymentTypeSelector);



// WEBPACK FOOTER //
// ./src/components/school_money/basket/checkout/secure_trading/PaymentTypeSelector.js